import IntranetLayout from "@/components/IntranetLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { GraduationCap, Plus, AlertTriangle, CheckCircle2, Clock, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

const statusBadge: Record<string, { label: string; variant: "default" | "destructive" | "secondary" | "outline" }> = {
  valid: { label: "Valid", variant: "default" },
  expiring: { label: "Expiring Soon", variant: "secondary" },
  expired: { label: "Expired", variant: "destructive" },
};

const EmployeeTraining = () => {
  const { user, isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ certification_name: "", issuing_body: "", issued_date: "", expiry_date: "", status: "valid" });

  const { data: records = [], isLoading } = useQuery({
    queryKey: ["training-records"],
    queryFn: async () => {
      const { data, error } = await supabase.from("training_records").select("*").order("expiry_date", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const addMutation = useMutation({
    mutationFn: async (values: typeof form) => {
      const { error } = await supabase.from("training_records").insert({ ...values, user_id: user!.id, issued_date: values.issued_date || null, expiry_date: values.expiry_date || null });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["training-records"] });
      toast.success("Record added");
      setForm({ certification_name: "", issuing_body: "", issued_date: "", expiry_date: "", status: "valid" });
      setDialogOpen(false);
    },
    onError: (e) => toast.error(e.message),
  });

  const expiringCount = records.filter((r: any) => {
    if (!r.expiry_date) return false;
    const diff = new Date(r.expiry_date).getTime() - Date.now();
    return diff > 0 && diff < 30 * 86400000;
  }).length;
  const expiredCount = records.filter((r: any) => r.status === "expired" || (r.expiry_date && new Date(r.expiry_date) < new Date())).length;

  return (
    <IntranetLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-foreground">Training & Certifications</h1>
          <p className="text-sm text-muted-foreground font-body mt-1">Track crew certifications, qualifications, and renewal dates</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => toast.success("Created: Add Record", { description: "New entry added." })} size="sm" className="bg-teal hover:bg-teal-light text-white gap-2"><Plus className="w-4 h-4" /> Add Record</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Certification</DialogTitle></DialogHeader>
            <form onSubmit={(e) => { e.preventDefault(); addMutation.mutate(form); }} className="space-y-4">
              <Input placeholder="Certification Name" value={form.certification_name} onChange={(e) => setForm({ ...form, certification_name: e.target.value })} required />
              <Input placeholder="Issuing Body" value={form.issuing_body} onChange={(e) => setForm({ ...form, issuing_body: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-muted-foreground font-body block mb-1">Issued Date</label><Input type="date" value={form.issued_date} onChange={(e) => setForm({ ...form, issued_date: e.target.value })} /></div>
                <div><label className="text-xs text-muted-foreground font-body block mb-1">Expiry Date</label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} /></div>
              </div>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="valid">Valid</SelectItem>
                  <SelectItem value="expiring">Expiring Soon</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
              <Button type="submit" className="w-full bg-teal hover:bg-teal-light text-white" disabled={addMutation.isPending}>Add Record</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-9 h-9 rounded-lg bg-teal/10 flex items-center justify-center"><CheckCircle2 className="w-4.5 h-4.5 text-teal" /></div><div><p className="text-xl font-display text-foreground">{records.length}</p><p className="text-xs text-muted-foreground font-body">Total Records</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center"><Clock className="w-4.5 h-4.5 text-amber-500" /></div><div><p className="text-xl font-display text-foreground">{expiringCount}</p><p className="text-xs text-muted-foreground font-body">Expiring Soon</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-9 h-9 rounded-lg bg-destructive/10 flex items-center justify-center"><AlertTriangle className="w-4.5 h-4.5 text-destructive" /></div><div><p className="text-xl font-display text-foreground">{expiredCount}</p><p className="text-xs text-muted-foreground font-body">Expired</p></div></CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-teal/30 border-t-teal rounded-full animate-spin" /></div>
          ) : records.length === 0 ? (
            <div className="p-12 text-center"><GraduationCap className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground font-body">No training records yet</p></div>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Certification</TableHead><TableHead>Issuing Body</TableHead><TableHead>Issued</TableHead><TableHead>Expires</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {records.map((r: any) => {
                  const cfg = statusBadge[r.status] || statusBadge.valid;
                  return (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium text-sm">{r.certification_name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{r.issuing_body || "—"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{r.issued_date ? new Date(r.issued_date).toLocaleDateString() : "—"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{r.expiry_date ? new Date(r.expiry_date).toLocaleDateString() : "—"}</TableCell>
                      <TableCell><Badge variant={cfg.variant}>{cfg.label}</Badge></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </IntranetLayout>
  );
};

export default EmployeeTraining;
